/**
 * Audio en WebAudio direct : sons d'événements avec garde temporelle (pas d'empilement de confirmations),
 * boucle moteur modulée par le vol (SpaceShipPawn.cpp:1427-1437), atténuation simple avec la distance.
 * Le contexte n'est débloqué qu'après une interaction (politique d'autoplay des navigateurs).
 */
import { assetUrl } from '../assets';
import type { SimEvent } from '../sim/events';
import type { Simulation } from '../sim/simulation';
import { Vec3 } from '../sim/core/math';

const NAMES = ['Defeat', 'EngineLoop', 'Explosion', 'HitConfirm', 'KillConfirm', 'Laser', 'MissileLaunch', 'Pickup', 'TargetLocked', 'UIClick', 'Victory'] as const;
type SoundName = (typeof NAMES)[number];

export class AudioSystem {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly buffers = new Map<SoundName, AudioBuffer>();
  private engine: { source: AudioBufferSourceNode; gain: GainNode } | null = null;
  private readonly lastPlayed = new Map<string, number>();
  private enabled = true;
  private volume = 0.8;

  /** À appeler depuis un geste utilisateur (clic du menu). */
  async unlock(): Promise<void> {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
      } catch {
        return;
      }
      this.master = this.ctx.createGain();
      this.master.connect(this.ctx.destination);
      this.applyVolume();
      await Promise.all(NAMES.map(async (name) => {
        try {
          const response = await fetch(assetUrl(`audio/S_${name}.wav`));
          const data = await response.arrayBuffer();
          this.buffers.set(name, await this.ctx!.decodeAudioData(data));
        } catch {
          // Son manquant : le jeu continue sans lui.
        }
      }));
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume().catch(() => undefined);
  }

  configure(enabled: boolean, volume: number): void {
    this.enabled = enabled;
    this.volume = volume;
    this.applyVolume();
  }

  private applyVolume(): void {
    if (this.master) this.master.gain.value = this.enabled ? this.volume : 0;
  }

  play(name: SoundName, volume = 1, pitch = 1, guard = 0, key: string = name): void {
    const ctx = this.ctx;
    const buffer = this.buffers.get(name);
    if (!ctx || !this.master || !buffer || !this.enabled) return;
    const now = ctx.currentTime;
    if (guard > 0 && now - (this.lastPlayed.get(key) ?? -10) < guard) return;
    this.lastPlayed.set(key, now);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = pitch;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    source.connect(gain).connect(this.master);
    source.start();
  }

  ui(): void {
    this.play('UIClick', 0.5, 1, 0.04);
  }

  startEngine(): void {
    const ctx = this.ctx;
    const buffer = this.buffers.get('EngineLoop');
    if (!ctx || !this.master || !buffer || this.engine) return;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    source.connect(gain).connect(this.master);
    source.start();
    this.engine = { source, gain };
  }

  stopEngine(): void {
    if (!this.engine) return;
    try { this.engine.source.stop(); } catch { /* déjà arrêté */ }
    this.engine = null;
  }

  /** Volume et hauteur du moteur selon l'enveloppe de vol. */
  updateEngine(sim: Simulation | null): void {
    if (!this.engine || !this.ctx) return;
    const t = this.ctx.currentTime;
    if (!sim || !sim.player.alive) {
      this.engine.gain.gain.setTargetAtTime(0, t, 0.1);
      return;
    }
    const player = sim.player;
    const f = player.flight;
    const m = player.motor;
    const forward = Math.max(0, f.signedForwardSpeed);
    const reverseAlpha = Math.min(1, Math.max(0, -f.signedForwardSpeed) / Math.max(m.reverseMaxSpeed, 1));
    const feedback = Math.min(1, 0.58 * Math.pow(Math.min(1, forward / m.cruiseMaxSpeed), 1.35) + 0.42 * Math.min(1, Math.max(0, (forward - m.cruiseMaxSpeed) / Math.max(m.boostMaxSpeed - m.cruiseMaxSpeed, 1))));
    const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
    const volume = f.braking ? lerp(0.22, 0.38, reverseAlpha) : f.reversing ? lerp(0.24, 0.46, reverseAlpha) : lerp(0.18, f.boosting ? 0.78 : 0.5, feedback);
    const pitch = f.braking ? lerp(0.72, 0.6, reverseAlpha) : f.reversing ? lerp(0.74, 0.94, reverseAlpha) : lerp(0.72, f.boosting ? 1.46 : 1.14, feedback);
    this.engine.gain.gain.setTargetAtTime(volume * 0.55, t, 0.08);
    this.engine.source.playbackRate.setTargetAtTime(pitch, t, 0.08);
  }

  handleEvents(sim: Simulation, events: SimEvent[]): void {
    const listener = sim.player.position;
    const atten = (p: readonly number[]) => {
      const d = Vec3.from(p).distanceTo(listener);
      return 1 / (1 + Math.max(0, d - 20) / 90);
    };
    for (const e of events) {
      switch (e.type) {
        case 'laserFired':
          this.play('Laser', e.width > 0.3 ? 0.34 : 0.22, (e.width > 0.3 ? 0.78 : 1) * (0.96 + Math.random() * 0.08), 0.03, 'laser');
          break;
        case 'damage':
          if (e.source === 'player' && e.applied > 0) {
            if (e.killed) this.play('KillConfirm', 0.7, 1, 0.12);
            else this.play('HitConfirm', 0.35, 1, 0.09);
          } else if (e.targetKind === 'player' && e.applied > 0) {
            this.play('Explosion', Math.min(0.6, 0.2 + e.applied / 50), 1.6, 0.12, 'playerHit');
          } else if (e.targetKind === 'player' && e.absorbed > 0) {
            this.play('HitConfirm', Math.min(0.5, 0.2 + e.absorbed / 60), 0.55, 0.1, 'shieldHit');
          }
          break;
        case 'explosion':
          this.play('Explosion', (e.weapon === 'swarm' ? 0.42 : e.weapon === 'detonation' ? 0.7 : 1) * atten(e.position), e.weapon === 'swarm' ? 1.18 : e.weapon === 'detonation' ? 1.08 : 0.84, e.weapon === 'swarm' ? 0.05 : 0.02, 'explosion');
          break;
        case 'shipDestroyed':
          if (e.kind !== 'player') this.play('Explosion', (e.kind === 'boss' ? 1.25 : 0.8) * atten(e.position), e.kind === 'boss' ? 0.68 : 1.05);
          break;
        case 'missileLaunched':
          this.play('MissileLaunch', e.kind === 'swarm' ? 0.95 : 0.75, e.kind === 'swarm' ? 1.14 : 1);
          break;
        case 'enemyFired':
          this.play('Laser', (e.kind === 'railgun' ? 0.78 : e.source === 'boss' ? 0.72 : 0.42) * atten(e.origin), e.kind === 'railgun' ? 0.72 : e.source === 'boss' ? 0.68 : 0.78, 0.04, `enemy-${e.kind}`);
          break;
        case 'lockChanged':
          if (e.state === 'locked') this.play('TargetLocked', 0.5, 1, 0.2);
          break;
        case 'pickupCollected':
          this.play('Pickup', 0.7);
          break;
        case 'droneDive':
          if (e.phase === 'warn') this.play('TargetLocked', 0.55, 1.7, 0.15, 'droneWarn');
          break;
        case 'overheat':
          this.play('UIClick', 0.7, 0.5, 0.3);
          break;
        case 'boostDepleted':
          this.play('UIClick', 0.5, 0.7, 0.5);
          break;
        case 'jumpDrive':
          if (e.phase === 'charging') this.play('TargetLocked', 0.55, 0.55);
          else if (e.phase === 'cancelled' || e.phase === 'blocked') this.play('UIClick', 0.6, 0.7);
          break;
        case 'shieldBroken':
          if (e.shipId === sim.player.id) this.play('TargetLocked', 0.6, 0.5, 0.3);
          break;
        case 'emergencyRepair':
          this.play('Pickup', 0.8, 1.2);
          break;
        case 'score':
          this.play('HitConfirm', 0.32, 1 + (e.multiplier - 1) * 0.12, 0.05, 'streak');
          break;
        case 'dock':
          if (e.phase === 'docked') this.play('Pickup', 0.8, 0.8);
          if (e.phase === 'launching') this.play('MissileLaunch', 0.9, 0.55);
          break;
        case 'playerDown':
          this.play('Explosion', 1, 0.6);
          break;
        case 'respawn':
          this.play('TargetLocked', 0.6, 0.8);
          break;
        case 'missionEnded':
          this.play(e.outcome === 'victory' ? 'Victory' : 'Defeat', 0.8);
          break;
        default:
          break;
      }
    }
  }
}
